'use client'

import { useState, useCallback, useEffect, useRef } from 'react'

// ========================================
// CONFIRMATION DIALOG (replaces window.confirm)
// ========================================

interface ConfirmDialogProps {
    open: boolean
    title: string
    message: string
    confirmLabel?: string
    cancelLabel?: string
    variant?: 'danger' | 'warning' | 'info'
    onConfirm: () => void
    onCancel: () => void
}

export function ConfirmDialog({
    open,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'danger',
    onConfirm,
    onCancel,
}: ConfirmDialogProps) {
    const dialogRef = useRef<HTMLDivElement>(null)

    // Close on Escape
    useEffect(() => {
        if (!open) return
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCancel()
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [open, onCancel])

    // Focus trap
    useEffect(() => {
        if (open && dialogRef.current) {
            dialogRef.current.focus()
        }
    }, [open])

    if (!open) return null

    const variantStyles = {
        danger: {
            icon: (
                <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
            ),
            iconBg: 'bg-red-500/10',
            confirmBtn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
        },
        warning: {
            icon: (
                <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                </svg>
            ),
            iconBg: 'bg-yellow-500/10',
            confirmBtn: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
        },
        info: {
            icon: (
                <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                </svg>
            ),
            iconBg: 'bg-blue-500/10',
            confirmBtn: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
        },
    }

    const style = variantStyles[variant]

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onCancel}
            />

            {/* Dialog */}
            <div
                ref={dialogRef}
                tabIndex={-1}
                className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl ring-1 ring-gray-900/5 dark:ring-white/10 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-message"
            >
                <div className="p-6">
                    <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full ${style.iconBg} flex items-center justify-center`}>
                            {style.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 id="confirm-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                                {title}
                            </h3>
                            <p id="confirm-dialog-message" className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                {message}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        onClick={onConfirm}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${style.confirmBtn}`}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    )
}


// ========================================
// TOAST NOTIFICATION (replaces window.alert)
// ========================================

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

interface ToastProps {
    id: string
    message: string
    variant: ToastVariant
    duration?: number
    onClose: (id: string) => void
}

function Toast({ id, message, variant, duration = 4000, onClose }: ToastProps) {
    useEffect(() => {
        const timer = setTimeout(() => onClose(id), duration)
        return () => clearTimeout(timer)
    }, [id, duration, onClose])

    const variantStyles = {
        success: {
            bg: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
            text: 'text-green-800 dark:text-green-200',
            icon: (
                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
            ),
        },
        error: {
            bg: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800',
            text: 'text-red-800 dark:text-red-200',
            icon: (
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                </svg>
            ),
        },
        warning: {
            bg: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
            text: 'text-yellow-800 dark:text-yellow-200',
            icon: (
                <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
            ),
        },
        info: {
            bg: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
            text: 'text-blue-800 dark:text-blue-200',
            icon: (
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                </svg>
            ),
        },
    }

    const style = variantStyles[variant]

    return (
        <div
            className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg max-w-sm w-full ${style.bg} animate-in slide-in-from-top-2 fade-in duration-300`}
            role="alert"
        >
            <div className="flex-shrink-0 mt-0.5">{style.icon}</div>
            <p className={`flex-1 text-sm font-medium ${style.text}`}>{message}</p>
            <button
                onClick={() => onClose(id)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    )
}


// ========================================
// TOAST CONTAINER
// ========================================

interface ToastItem {
    id: string
    message: string
    variant: ToastVariant
    duration?: number
}

export function ToastContainer({ toasts, onClose }: { toasts: ToastItem[]; onClose: (id: string) => void }) {
    if (toasts.length === 0) return null

    return (
        <div className="fixed top-4 right-4 z-[200] flex flex-col gap-2">
            {toasts.map((toast) => (
                <Toast key={toast.id} {...toast} onClose={onClose} />
            ))}
        </div>
    )
}


// ========================================
// HOOKS (for easy usage in components)
// ========================================

interface ConfirmState {
    open: boolean
    title: string
    message: string
    confirmLabel: string
    variant: 'danger' | 'warning' | 'info'
    resolve: ((confirmed: boolean) => void) | null
}

/**
 * Hook that replaces window.confirm with an inline dialog.
 * 
 * Usage:
 * ```tsx
 * const { confirm, ConfirmDialogComponent } = useConfirmDialog()
 * 
 * const handleDelete = async () => {
 *   const confirmed = await confirm({
 *     title: 'Delete Entry',
 *     message: 'Are you sure? This cannot be undone.',
 *     confirmLabel: 'Delete',
 *     variant: 'danger'
 *   })
 *   if (!confirmed) return
 *   // ... proceed with deletion
 * }
 * 
 * return <>{ConfirmDialogComponent}</>
 * ```
 */
export function useConfirmDialog() {
    const [state, setState] = useState<ConfirmState>({
        open: false,
        title: '',
        message: '',
        confirmLabel: 'Confirm',
        variant: 'danger',
        resolve: null,
    })

    const confirm = useCallback(
        (opts: {
            title: string
            message: string
            confirmLabel?: string
            variant?: 'danger' | 'warning' | 'info'
        }): Promise<boolean> => {
            return new Promise((resolve) => {
                setState({
                    open: true,
                    title: opts.title,
                    message: opts.message,
                    confirmLabel: opts.confirmLabel || 'Confirm',
                    variant: opts.variant || 'danger',
                    resolve,
                })
            })
        },
        []
    )

    const handleConfirm = useCallback(() => {
        state.resolve?.(true)
        setState((prev) => ({ ...prev, open: false, resolve: null }))
    }, [state.resolve])

    const handleCancel = useCallback(() => {
        state.resolve?.(false)
        setState((prev) => ({ ...prev, open: false, resolve: null }))
    }, [state.resolve])

    const ConfirmDialogComponent = (
        <ConfirmDialog
            open={state.open}
            title={state.title}
            message={state.message}
            confirmLabel={state.confirmLabel}
            variant={state.variant}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
        />
    )

    return { confirm, ConfirmDialogComponent }
}

/**
 * Hook that replaces window.alert with inline toast notifications.
 * 
 * Usage:
 * ```tsx
 * const { showToast, ToastContainerComponent } = useToast()
 * 
 * showToast('Entry deleted successfully', 'success')
 * showToast('Failed to save', 'error')
 * 
 * return <>{ToastContainerComponent}</>
 * ```
 */
export function useToast() {
    const [toasts, setToasts] = useState<ToastItem[]>([])

    const showToast = useCallback(
        (message: string, variant: ToastVariant = 'info', duration?: number) => {
            const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
            setToasts((prev) => [...prev, { id, message, variant, duration }])
        },
        []
    )

    const closeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
    }, [])

    const ToastContainerComponent = <ToastContainer toasts={toasts} onClose={closeToast} />

    return { showToast, ToastContainerComponent }
}
